import { useEffect, useState } from 'react'
import { API_BASE } from '../lib/api'

const RANGES = [
  { label: '7 days', value: '7' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
  { label: 'All', value: '' },
]

function fmtDate(d) {
  return d ? new Date(d).toLocaleString() : '—'
}

export default function AdminQrScansPanel({ adminToken }) {
  const [days, setDays] = useState('30')
  const [rows, setRows] = useState([])
  const [totals, setTotals] = useState({ qrScans: 0, linkVisits: 0, registrations: 0 })
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')
  const [search, setSearch] = useState('')
  const [openCode, setOpenCode] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  async function load() {
    setLoading(true)
    setMsg('')
    try {
      const r = await fetch(`${API_BASE}/affiliate/admin/qr-scans?days=${days}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.message)
      setRows(d.referrers || [])
      setTotals(d.totals || { qrScans: 0, linkVisits: 0, registrations: 0 })
    } catch (err) {
      setMsg(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function loadDetail(code) {
    setDetailLoading(true)
    setDetail(null)
    try {
      const r = await fetch(`${API_BASE}/affiliate/admin/qr-scans/${encodeURIComponent(code)}?days=${days}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.message)
      setDetail(d)
    } catch (err) {
      setMsg(err.message)
    } finally {
      setDetailLoading(false)
    }
  }

  useEffect(() => { load() }, [days])
  useEffect(() => { if (openCode) loadDetail(openCode) }, [openCode, days])

  const q = search.trim().toLowerCase()
  const filtered = q
    ? rows.filter(r => [r.name, r.email, r.referralCode].some(v => v?.toLowerCase().includes(q)))
    : rows

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="title-font text-xl font-bold text-gray-900">QR Scans &amp; Referral Visits</h2>
        <div className="flex flex-wrap gap-2">
          {RANGES.map(r => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                days === r.value ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' : 'border border-orange-300 text-orange-700 hover:bg-orange-50'
              }`}
            >
              {r.label}
            </button>
          ))}
          <button onClick={load} className="rounded-full border border-gray-300 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50">↻</button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          { label: 'QR Scans', value: totals.qrScans },
          { label: 'Link Visits', value: totals.linkVisits },
          { label: 'Registrations', value: totals.registrations },
        ].map(s => (
          <div key={s.label} className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{s.label}</p>
            <p className="mt-1 text-2xl font-extrabold text-gray-900">{s.value}</p>
          </div>
        ))}
      </div>

      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by name, email or referral code"
        className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm"
      />

      {msg && <p className="text-sm font-medium text-red-500">{msg}</p>}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-400">No referral activity in this period.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div key={r.referralCode} className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {r.name || 'Unknown referrer'}{' '}
                    {r.email && <span className="text-gray-400 font-normal text-xs">({r.email})</span>}
                  </p>
                  <p className="text-xs text-gray-500">
                    Code <span className="font-mono font-bold text-orange-600">{r.referralCode}</span> · Last visit {fmtDate(r.lastVisitAt)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
                  <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-700">📷 {r.qrScans} scans</span>
                  <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-700">🔗 {r.linkVisits} link</span>
                  <span className="rounded-full bg-green-100 px-3 py-1 text-green-700">✅ {r.registrations} registered</span>
                  <button
                    onClick={() => setOpenCode(openCode === r.referralCode ? null : r.referralCode)}
                    className="rounded-lg border border-orange-300 px-3 py-1 font-semibold text-orange-700 hover:bg-orange-50"
                  >
                    {openCode === r.referralCode ? 'Hide' : 'Details'}
                  </button>
                </div>
              </div>

              {r.locations.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {r.locations.slice(0, 8).map(l => (
                    <span key={l.city} className="rounded-full bg-orange-50 px-2.5 py-0.5 text-xs text-gray-700">
                      📍 {l.city} · {l.count}
                    </span>
                  ))}
                  {r.locations.length > 8 && <span className="text-xs text-gray-400">+{r.locations.length - 8} more</span>}
                </div>
              )}

              {openCode === r.referralCode && (
                <div className="rounded-xl bg-orange-50 p-3 space-y-4">
                  {detailLoading || !detail ? (
                    <p className="text-xs text-gray-400">Loading details…</p>
                  ) : (
                    <>
                      <div>
                        <p className="mb-1 text-xs font-semibold text-gray-700">Registered users ({detail.registeredUsers.length})</p>
                        {detail.registeredUsers.length === 0 ? (
                          <p className="text-xs text-gray-400">None yet.</p>
                        ) : (
                          <ul className="space-y-0.5 text-xs text-gray-700">
                            {detail.registeredUsers.map(u => (
                              <li key={u.id}>{u.name} <span className="text-gray-400">({u.email})</span> · {fmtDate(u.createdAt)}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <p className="mb-1 text-xs font-semibold text-gray-700">Visits ({detail.visits.length}{detail.visits.length === 500 ? ', latest 500' : ''})</p>
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[640px] text-left text-xs">
                            <thead className="text-gray-500">
                              <tr>
                                <th className="py-1 pr-3">When</th>
                                <th className="py-1 pr-3">Type</th>
                                <th className="py-1 pr-3">Location</th>
                                <th className="py-1 pr-3">Device</th>
                                <th className="py-1 pr-3">Visitor</th>
                              </tr>
                            </thead>
                            <tbody className="text-gray-700">
                              {detail.visits.map(v => (
                                <tr key={v.id} className="border-t border-orange-100 align-top">
                                  <td className="py-1 pr-3 whitespace-nowrap">{fmtDate(v.createdAt)}</td>
                                  <td className="py-1 pr-3">{v.referralMedium === 'qr' ? '📷 QR' : '🔗 Link'}</td>
                                  <td className="py-1 pr-3">
                                    {v.address || v.city || 'Unknown'}
                                    {v.latitude != null && v.longitude != null && (
                                      <>
                                        {' '}
                                        <a
                                          href={`https://www.google.com/maps?q=${v.latitude},${v.longitude}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-orange-600 underline"
                                        >
                                          map
                                        </a>
                                      </>
                                    )}
                                  </td>
                                  <td className="py-1 pr-3">{[v.deviceType, v.operatingSystem, v.primaryBrowser].filter(Boolean).join(' · ') || '—'}</td>
                                  <td className="py-1 pr-3">{v.user?.name || 'Anonymous'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
